<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import {QCardSection} from "quasar";
import {DashboardWidgetProps} from "@/utils/presenters/interfaces.ts";
import {computed, nextTick, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";

const props = defineProps<DashboardWidgetProps>();
const {darkMode} = storeToRefs(useNamaFlagsStore());
const widgetId: string = (props.chartLine.widgetId ?? 'html');

const htmlTemplate = computed<string>(() => {
  const parameters: Record<any, any> = props.chartLine.parameters ?? {};
  const value = Translator.isArabic() ? parameters.HTMLArabicTemplate : parameters.HTMLEnglishTemplate;
  return value ?? '';
});
const isOpenInIFrame = computed<boolean>(() => props.chartLine.parameters?.HTMLOpenInIFrame === "true");
const iframeRef = ref<HTMLIFrameElement | null>(null);
const htmlContainerRef = ref<HTMLElement | null>(null);

function onFrameLoaded() {
  if (iframeRef.value?.contentDocument) {
    const iframeDoc = iframeRef.value.contentDocument;
    const style = iframeDoc.createElement('style');
    if (darkMode.value) {
      style.textContent = `
          *{
            scrollbar-color: #8b8b8b #2c2c2c;
          }
        `;
    }
    iframeDoc.head.appendChild(style);
  }
}

function enableScripts(wrap: HTMLElement | null) {
  if (!wrap) return;
  const scripts = wrap.getElementsByTagName('script');
  const scriptArray = Array.from(scripts);
  for (const node of scriptArray) {
    const parent = node.parentElement;
    if (!parent) continue;
    const replacement = document.createElement('script');
    replacement.async = node.async;
    if (node.src) replacement.src = node.src;
    if (node.type) replacement.type = node.type;
    if (node.textContent) replacement.textContent = node.textContent;
    parent.insertBefore(replacement, node);
    parent.removeChild(node);
  }
}

watch([htmlContainerRef, htmlTemplate], async ([el]) => {
  if (!el || isOpenInIFrame.value) return;
  await nextTick();
  enableScripts(el);
}, {immediate: true, flush: 'post'});
</script>

<template>
  <div :key="chartKey + widgetId" class="tw-h-full">
    <q-card-section
      class="tw-grow tw-h-full tw-w-full  tw-text-base tw-font-normal tw-p-0">
      <div class="tw-w-full tw-h-full">
        <q-card-section class="tw-h-full tw-w-full tw-p-0" v-if="isOpenInIFrame">
          <iframe ref="iframeRef" :srcdoc="htmlTemplate" class="tw-w-full tw-h-full" @load="onFrameLoaded"/>
        </q-card-section>
        <q-card-section v-else
                        class="tw-max-h-full tw-w-full tw-overflow-auto  tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-text-base tw-font-normal">
          <div ref="htmlContainerRef" v-html="htmlTemplate" class="tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"/>
        </q-card-section>
      </div>
    </q-card-section>
  </div>
</template>

<style>

</style>
